import {IconsEnum} from '../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../shared-modules/shared-icons/models/icons-colour.enum';
import {IStore} from '../../../shared/shared-models/store/store';
import {SharedModalUtils} from '../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {
  CollectionStoresSettingsService,
} from '../../../shared/shared-services/firebase/collection-stores-settings.service';
import {IAdvancedFilterGroup} from '../models/general/advanced-filter-group.interface';
import {
  type AdvancedFilterGridCategory,
  matchesAdvancedFilterGridCategory,
} from '../models/general/advanced-filter-grid.constants';

/**
 * If the target store already has filters with the same names, asks how to proceed.
 * Returns filter ids to copy, or null when the user cancels.
 */
export async function resolveAdvancedFilterIdsAfterDuplicateWarning(
  modalUtils: SharedModalUtils,
  storesSettings: CollectionStoresSettingsService,
  selectedFilters: IAdvancedFilterGroup[],
  target: IStore,
  category: AdvancedFilterGridCategory,
  categoryLabel: string,
): Promise<number[] | null> {
  const allIds: number[] = selectedFilters.map((filter: IAdvancedFilterGroup): number => filter.id);

  let targetFilters: IAdvancedFilterGroup[];
  try {
    const allTargetFilters: IAdvancedFilterGroup[] = await storesSettings.getStoreSettingsAdvancedFilterGroups(target);
    targetFilters = allTargetFilters.filter((filter: IAdvancedFilterGroup): boolean =>
      matchesAdvancedFilterGridCategory(filter.grid, category),
    );
  } catch {
    return allIds;
  }

  const targetNames = new Set<string>(
    targetFilters
      .map((filter: IAdvancedFilterGroup): string => (filter.name ?? '').trim().toLowerCase())
      .filter((name: string): boolean => name.length > 0),
  );

  const duplicates: IAdvancedFilterGroup[] = selectedFilters.filter((filter: IAdvancedFilterGroup): boolean => {
    const name: string = (filter.name ?? '').trim().toLowerCase();
    return name.length > 0 && targetNames.has(name);
  });

  if (duplicates.length === 0) {
    return allIds;
  }

  const nonDuplicateIds: number[] = selectedFilters
    .filter((filter: IAdvancedFilterGroup): boolean => !duplicates.includes(filter))
    .map((filter: IAdvancedFilterGroup): number => filter.id);

  const duplicateNames: string = duplicates
    .map((filter: IAdvancedFilterGroup): string => `"${filter.name}"`)
    .join(', ');
  const canSkipDuplicates: boolean = nonDuplicateIds.length > 0;
  const skipLabel: string = `Skip duplicates (copy ${nonDuplicateIds.length} of ${selectedFilters.length})`;

  const result = await modalUtils.createModal({
    modalTitleIcon: IconsEnum.infoSlabCircleOutline,
    modalTitleIconColor: IconColourEnum.yellow,
    modalTitle: 'Duplicate Filter Names',
    contextDescription: [
      `"${target.name}" already has ${categoryLabel.toLowerCase()} named: ${duplicateNames}.`,
      canSkipDuplicates
        ? `Copy all includes the duplicates. "${skipLabel}" copies only the unique names. Cancel copies nothing.`
        : 'All selected filters already exist in the target store by name. Copy them anyway, or cancel to copy nothing.',
    ],
    buttonAcceptText: 'Copy all',
    buttonSecondary: canSkipDuplicates,
    buttonSecondaryText: skipLabel,
    buttonCloseText: 'Cancel',
    buttonAccept: true,
    buttonClose: true,
    buttonCloseCross: true,
    backdropDismiss: false,
    cssClass: ['shared-basic-modal-css'],
  });

  if (result.data?.buttonPressed === 'button-accept') {
    return allIds;
  }
  if (result.data?.buttonPressed === 'button-secondary' && canSkipDuplicates) {
    return nonDuplicateIds;
  }
  return null;
}
